// @flow

import _ from 'lodash';

import { pluralize } from '@zapier/common-utils';

import { compareBySortOrderOrTitle } from 'app/common/CommonUtils';
import { getDaysRemainingMessage } from 'app/dashboard/utils/ZapWarningUtils';
import {
  AUTHS_URL,
  CREDIT_CARD_URL,
  PLANS_URL,
} from 'app/dashboard/utils/constants';
import { HeldReasons, REPLAY_STEP } from 'app/history/utils/constants';

type HeldTaskBannerProps = {
  title: string,
  daysRemainText: string,
  msg: string,
  buttonText: string,
};

type CountsByHeldReasonType = {
  paymentIssue?: number,
  plan_limit?: number,
  stale_auth?: number,
  too_many?: number,
  throttled?: number,
};

type SnapshotType = {
  countsByHeldReason: CountsByHeldReasonType,
  staleAuthServiceNames: string[],
  totalHeldTaskCount: number,
};

export const getHeldTaskBannerTitle = ({
  numOfStepsDone,
  numOfStepsLeft,
  totalHeldTaskCount,
}: {
  totalHeldTaskCount: number,
  numOfStepsDone: number,
  numOfStepsLeft: number,
}) => {
  const tasks = pluralize('Task', totalHeldTaskCount);
  // 'Cuz currently we can only see 10,000 tasks in TH page, and we want the banner count to match TH count.
  const visibleTaskLimit = 10000;
  const visibleTaskLimitString = `${visibleTaskLimit.toLocaleString()}+`;
  const totalNumOfHeldTasks =
    totalHeldTaskCount > visibleTaskLimit
      ? visibleTaskLimitString
      : totalHeldTaskCount.toLocaleString();

  // numOfStepsDone is the key one to pay attention to since it will be updated as user completes each step (tracked by FE only)
  if (numOfStepsDone > 0 && numOfStepsLeft > 1) {
    return `Great job! ${numOfStepsLeft} more fixes to play your ${totalNumOfHeldTasks} held ${tasks}!`;
  }

  // Only 1 step left (replay tasks step)
  if (numOfStepsDone > 0 && numOfStepsLeft === 1) {
    return `Great job! You can now play your held ${tasks}!`;
  }

  return `Don't lose your data from ${totalNumOfHeldTasks} held ${tasks}!`;
};

export const getHeldTaskBannerProps = ({
  heldTasksStartsExpiringOn,
  totalHeldTaskCount,
  sameExpirationDate,
  numOfStepsDone,
  numOfStepsLeft,
}: {
  heldTasksStartsExpiringOn: string,
  totalHeldTaskCount: number,
  sameExpirationDate?: boolean,
  numOfStepsDone: number,
  numOfStepsLeft: number,
}) => {
  if (!heldTasksStartsExpiringOn || !totalHeldTaskCount) {
    return {};
  }

  const { daysRemainText, msg } = getDaysRemainingMessage(
    heldTasksStartsExpiringOn,
    sameExpirationDate
  );

  return ({
    title: getHeldTaskBannerTitle({
      numOfStepsDone,
      numOfStepsLeft,
      totalHeldTaskCount,
    }),
    daysRemainText,
    msg,
    buttonText: numOfStepsDone > 0 ? 'Fix the Rest' : 'Help Me Fix It',
  }: HeldTaskBannerProps);
};

export type ActionableHeldTasksProps = {
  isActionable: true,
  isDone: boolean,
  key: string,
  cta: string,
  ctaUrl: ?string,
  description: string,
};

type NotActionableHeldTasksProps = {
  isActionable: false,
};

type HeldTaskStepProps =
  | NotActionableHeldTasksProps
  | ActionableHeldTasksProps
  | ActionableHeldTasksProps[];

type HeldTasksPropsFn = ({
  count: number,
  currentStaleAuthServiceNames: Array<string>,
  snapshot?: SnapshotType,
  countsByHeldReason: CountsByHeldReasonType,
  rawCountsByHeldReason: CountsByHeldReasonType,
}) => ?HeldTaskStepProps;

export const isStepDone = ({
  reason,
  count,
  snapshot,
  currentStaleAuthServiceNames = [],
  prevStaleServiceName = '',
}: {
  reason: string,
  count: number,
  snapshot?: SnapshotType,
  currentStaleAuthServiceNames?: Array<string>,
  prevStaleServiceName?: string,
}) => {
  const prevCountsByHeldReason = _.get(snapshot, ['countsByHeldReason'], {});

  return (
    (prevCountsByHeldReason[reason] && !count) ||
    (reason === HeldReasons.STALE_AUTH &&
      !currentStaleAuthServiceNames.includes(prevStaleServiceName))
  );
};

export const getFloodProtectionDetails = (
  countsByHeldReason: CountsByHeldReasonType
) => {
  const { too_many, ...rest } = countsByHeldReason;
  const haveFloodProtectionHeldTasks =
    typeof too_many !== 'undefined' && too_many > 0;

  const haveNonFloodProtectionReasons = Object.keys(rest).some(
    reason => typeof rest[reason] !== 'undefined' && rest[reason] > 0
  );

  return {
    haveFloodProtectionHeldTasks,
    haveNonFloodProtectionReasons,
  };
};

export const getThrottledDetails = (
  countsByHeldReason: CountsByHeldReasonType
) => {
  const { throttled, ...rest } = countsByHeldReason;
  const haveThrottledHeldTasks =
    typeof throttled !== 'undefined' && throttled > 0;

  const haveNonThrottledReasons = Object.keys(rest).some(
    reason => typeof rest[reason] !== 'undefined' && rest[reason] > 0
  );

  return {
    haveThrottledHeldTasks,
    haveNonThrottledReasons,
  };
};

const getCommonProps = ({ key, isDone }) => ({
  isActionable: true,
  isDone,
  key,
  sortOrder: 4,
  cta: 'Confirm that you want to play these Tasks.',
  ctaUrl: null,
  description:
    'We held them because more Tasks than usual came through at once.',
});

export const stepShouldBeHidden = ({
  snapshot,
  countsByHeldReason,
  rawCountsByHeldReason,
}: {
  snapshot?: SnapshotType,
  countsByHeldReason: CountsByHeldReasonType,
  rawCountsByHeldReason: CountsByHeldReasonType,
}) => {
  const prevCountsByHeldReason = _.get(snapshot, ['countsByHeldReason'], {});

  const prevHaveNonFloodProtectionReasons = getFloodProtectionDetails(
    prevCountsByHeldReason
  ).haveNonFloodProtectionReasons;

  const prevHaveNonThrottledReasons = getThrottledDetails(
    prevCountsByHeldReason
  ).haveNonThrottledReasons;

  const {
    haveFloodProtectionHeldTasks,
    haveNonFloodProtectionReasons,
  } = getFloodProtectionDetails(countsByHeldReason);

  const {
    haveThrottledHeldTasks,
    haveNonThrottledReasons,
  } = getThrottledDetails(countsByHeldReason);

  const {
    haveNonFloodProtectionReasons: haveRawNonFloodProtectionReasons,
  } = getFloodProtectionDetails(rawCountsByHeldReason);
  const {
    haveNonThrottledReasons: haveRawNonThrottledReasons,
  } = getThrottledDetails(rawCountsByHeldReason);

  // When we have mixed messages including flood protection or throttling, the flood protection and messages do not show. We default to the "Replay your tasks message", so we're not telling the user to replay their tasks twice.
  return (
    (haveFloodProtectionHeldTasks &&
      (haveNonFloodProtectionReasons ||
        prevHaveNonFloodProtectionReasons ||
        haveRawNonFloodProtectionReasons)) ||
    (haveThrottledHeldTasks &&
      (haveNonThrottledReasons ||
        prevHaveNonThrottledReasons ||
        haveRawNonThrottledReasons))
  );
};

const HELD_TASKS_PROPS_FUNCTIONS: { [string]: HeldTasksPropsFn } = {
  [HeldReasons.PAYMENT_ISSUE]({ count, snapshot }) {
    const prevCountsByHeldReason = _.get(snapshot, ['countsByHeldReason'], {});

    const prevPaymentIssueCount = prevCountsByHeldReason[
      HeldReasons.PAYMENT_ISSUE
    ]
      ? prevCountsByHeldReason[HeldReasons.PAYMENT_ISSUE]
      : 0;
    const tasks = pluralize('Task', prevPaymentIssueCount);

    const isDone = isStepDone({
      reason: HeldReasons.PAYMENT_ISSUE,
      count,
      snapshot,
    });

    return ({
      isActionable: true,
      isDone,
      key: HeldReasons.PAYMENT_ISSUE,
      sortOrder: 1,
      cta: 'Update your card.',
      ctaUrl: CREDIT_CARD_URL,
      description: `We’re holding your ${tasks} because of a payment issue.`,
    }: ActionableHeldTasksProps);
  },

  [HeldReasons.PLAN_LIMIT]({ count, snapshot }) {
    const isDone = isStepDone({
      reason: HeldReasons.PLAN_LIMIT,
      count,
      snapshot,
    });

    return ({
      isActionable: true,
      isDone,
      key: HeldReasons.PLAN_LIMIT,
      sortOrder: 2,
      cta: 'Upgrade your Zapier plan.',
      ctaUrl: PLANS_URL,
      description: `You've exceeded your Zapier plan limits.`,
    }: ActionableHeldTasksProps);
  },

  [HeldReasons.STALE_AUTH]({ count, currentStaleAuthServiceNames, snapshot }) {
    const prevStaleAuthServiceNames = _.get(
      snapshot,
      ['staleAuthServiceNames'],
      []
    );

    const commonProps = {
      isActionable: true,
      sortOrder: 3,
      ctaUrl: AUTHS_URL,
      description: 'to your Zapier account.',
    };

    return prevStaleAuthServiceNames.map((prevStaleServiceName, i) => {
      const isDone = isStepDone({
        reason: HeldReasons.STALE_AUTH,
        count,
        snapshot,
        currentStaleAuthServiceNames,
        prevStaleServiceName,
      });

      return ({
        ...commonProps,
        isDone,
        key: `${HeldReasons.STALE_AUTH}-${i}`,
        cta: `Reconnect ${prevStaleServiceName}`,
      }: ActionableHeldTasksProps);
    });
  },

  [HeldReasons.TOO_MANY]({
    count,
    snapshot,
    countsByHeldReason,
    rawCountsByHeldReason,
  }) {
    // If there are other non-flood-protection held tasks, we want to hide this and only show the last step
    if (
      stepShouldBeHidden({
        snapshot,
        countsByHeldReason,
        rawCountsByHeldReason,
      })
    ) {
      return { isActionable: false };
    }

    const isDone = isStepDone({
      reason: HeldReasons.TOO_MANY,
      count,
      snapshot,
    });

    return (getCommonProps({
      isDone,
      key: HeldReasons.TOO_MANY,
    }): ActionableHeldTasksProps);
  },

  [HeldReasons.THROTTLED]({
    count,
    snapshot,
    countsByHeldReason,
    rawCountsByHeldReason,
  }) {
    // If there are other non-throttled held tasks, we want to hide this and only show the last step
    if (
      stepShouldBeHidden({
        snapshot,
        countsByHeldReason,
        rawCountsByHeldReason,
      })
    ) {
      return { isActionable: false };
    }

    const isDone = isStepDone({
      reason: HeldReasons.THROTTLED,
      count,
      snapshot,
    });

    return (getCommonProps({
      isDone,
      key: HeldReasons.THROTTLED,
    }): ActionableHeldTasksProps);
  },
};

export const getHeldTaskStepsProps = (
  currentStaleAuthServiceNames: string[],
  snapshot?: SnapshotType,
  countsByHeldReason: CountsByHeldReasonType,
  rawCountsByHeldReason: CountsByHeldReasonType,
  count: number,
  reason: string
) => {
  const fn = HELD_TASKS_PROPS_FUNCTIONS[reason];
  const prevCount = _.get(snapshot, ['countsByHeldReason', reason], 0);

  if (!fn || (!count && !prevCount)) {
    return { isActionable: false };
  }

  return fn({
    count,
    currentStaleAuthServiceNames,
    snapshot,
    countsByHeldReason,
    rawCountsByHeldReason,
  });
};

export const isLastStepActionable = ({
  snapshot,
  rawCountsByHeldReason,
}: {
  snapshot?: SnapshotType,
  rawCountsByHeldReason: CountsByHeldReasonType,
}) => {
  const prevCountsByHeldReason = _.get(snapshot, ['countsByHeldReason'], {});

  const currentHaveNonFloodProtectionReasons = getFloodProtectionDetails(
    rawCountsByHeldReason
  ).haveNonFloodProtectionReasons;

  const currentHaveNonThrottledReasons = getThrottledDetails(
    rawCountsByHeldReason
  ).haveNonThrottledReasons;

  const {
    haveFloodProtectionHeldTasks,
    haveNonFloodProtectionReasons,
  } = getFloodProtectionDetails(prevCountsByHeldReason);

  const {
    haveThrottledHeldTasks,
    haveNonThrottledReasons,
  } = getThrottledDetails(prevCountsByHeldReason);
  // Actionable == true
  // When we have mixed messages including flood protection or throttling, the flood protection and throttling messages do not show. We default to the "Replay your tasks message", so we're not telling the user to replay their tasks twice.

  // Actionable == false
  // Hide this step if held task is due to a single reason, either flood protection or throttling
  return (
    !(
      haveFloodProtectionHeldTasks &&
      !currentHaveNonFloodProtectionReasons &&
      !haveNonFloodProtectionReasons
    ) &&
    !(
      haveThrottledHeldTasks &&
      !currentHaveNonThrottledReasons &&
      !haveNonThrottledReasons
    )
  );
};

// This will always be the last step.
export const getLastStepProps = ({
  totalHeldTaskCount,
  snapshot,
  rawCountsByHeldReason,
}: {
  totalHeldTaskCount: number,
  snapshot?: SnapshotType,
  rawCountsByHeldReason: CountsByHeldReasonType,
}) => {
  return {
    isActionable: isLastStepActionable({ snapshot, rawCountsByHeldReason }),
    isDone: false,
    key: REPLAY_STEP,
    sortOrder: 90,
    cta: `Replay ${totalHeldTaskCount === 1 ? 'this' : 'these'} ${pluralize(
      'Task',
      totalHeldTaskCount
    )}.`,
    ctaUrl: null,
    description: 'You can choose to play them individually or in bulk.',
  };
};

export const getActionableStepsPropsList = ({
  countsByHeldReason,
  rawCountsByHeldReason,
  staleAuthServiceNames,
  totalHeldTaskCount,
  snapshot,
}: {
  countsByHeldReason: CountsByHeldReasonType,
  rawCountsByHeldReason: CountsByHeldReasonType,
  staleAuthServiceNames: string[],
  totalHeldTaskCount: number,
  snapshot?: SnapshotType,
}) => {
  const stepsPropsList = _.flattenDeep(
    _.map(
      countsByHeldReason,
      _.partial(
        getHeldTaskStepsProps,
        staleAuthServiceNames,
        snapshot,
        countsByHeldReason,
        rawCountsByHeldReason
      )
    )
  );

  const lastStepProps = getLastStepProps({
    totalHeldTaskCount,
    snapshot,
    countsByHeldReason,
    rawCountsByHeldReason,
  });

  return _.filter([...stepsPropsList, lastStepProps], 'isActionable').sort(
    compareBySortOrderOrTitle
  );
};

export const hasDifferencesFromSnapshot = (
  {
    countsByHeldReason,
    staleAuthServiceNames,
    totalHeldTaskCount,
  }: SnapshotType,
  snapshot?: SnapshotType
) => {
  return (
    !_.isEmpty(snapshot) &&
    !_.isEqual(
      {
        countsByHeldReason,
        staleAuthServiceNames,
        totalHeldTaskCount,
      },
      snapshot
    )
  );
};
